/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ExternalMCPHeaderDefinition,
  ExternalMCPHeaderDefinition$inboundSchema,
} from "./externalmcpheaderdefinition.js";
import {
  FunctionEnvironmentVariable,
  FunctionEnvironmentVariable$inboundSchema,
} from "./functionenvironmentvariable.js";
import {
  PromptTemplateEntry,
  PromptTemplateEntry$inboundSchema,
} from "./prompttemplateentry.js";
import { ResourceEntry, ResourceEntry$inboundSchema } from "./resourceentry.js";
import {
  SecurityVariable,
  SecurityVariable$inboundSchema,
} from "./securityvariable.js";
import {
  ServerVariable,
  ServerVariable$inboundSchema,
} from "./servervariable.js";
import { ToolEntry, ToolEntry$inboundSchema } from "./toolentry.js";
import { ToolsetOrigin, ToolsetOrigin$inboundSchema } from "./toolsetorigin.js";

export type ToolsetEntry = {
  /**
   * When the toolset was created.
   */
  createdAt: Date;
  /**
   * The ID of the custom domain to use for the toolset
   */
  customDomainId?: string | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  defaultEnvironmentSlug?: string | undefined;
  /**
   * Description of the toolset
   */
  description?: string | undefined;
  /**
   * The external MCP header definitions that are relevant to the toolset
   */
  externalMcpHeaderDefinitions?: Array<ExternalMCPHeaderDefinition> | undefined;
  /**
   * The function environment variables that are relevant to the toolset
   */
  functionEnvironmentVariables?: Array<FunctionEnvironmentVariable> | undefined;
  /**
   * The ID of the toolset
   */
  id: string;
  /**
   * Whether the toolset is enabled for MCP
   */
  mcpEnabled?: boolean | undefined;
  /**
   * Whether the toolset is public in MCP
   */
  mcpIsPublic?: boolean | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  mcpSlug?: string | undefined;
  /**
   * The name of the toolset
   */
  name: string;
  /**
   * The organization ID this toolset belongs to
   */
  organizationId: string;
  origin?: ToolsetOrigin | undefined;
  /**
   * The project ID this toolset belongs to
   */
  projectId: string;
  /**
   * The prompt templates in this toolset -- Note: these are actual prompts, as in MCP prompts
   */
  promptTemplates: Array<PromptTemplateEntry>;
  /**
   * The resource URNs in this toolset
   */
  resourceUrns: Array<string>;
  /**
   * The resources in this toolset
   */
  resources: Array<ResourceEntry>;
  /**
   * The security variables that are relevant to the toolset
   */
  securityVariables?: Array<SecurityVariable> | undefined;
  /**
   * The server variables that are relevant to the toolset
   */
  serverVariables?: Array<ServerVariable> | undefined;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  slug: string;
  /**
   * The mode to use for tool selection
   */
  toolSelectionMode: string;
  /**
   * The tool URNs in this toolset
   */
  toolUrns: Array<string>;
  /**
   * The tools in this toolset
   */
  tools: Array<ToolEntry>;
  /**
   * When the toolset was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const ToolsetEntry$inboundSchema: z.ZodMiniType<ToolsetEntry, unknown> =
  z.pipe(
    z.object({
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      custom_domain_id: z.optional(z.string()),
      default_environment_slug: z.optional(z.string()),
      description: z.optional(z.string()),
      external_mcp_header_definitions: z.optional(
        z.array(ExternalMCPHeaderDefinition$inboundSchema),
      ),
      function_environment_variables: z.optional(
        z.array(FunctionEnvironmentVariable$inboundSchema),
      ),
      id: z.string(),
      mcp_enabled: z.optional(z.boolean()),
      mcp_is_public: z.optional(z.boolean()),
      mcp_slug: z.optional(z.string()),
      name: z.string(),
      organization_id: z.string(),
      origin: z.optional(ToolsetOrigin$inboundSchema),
      project_id: z.string(),
      prompt_templates: z.array(PromptTemplateEntry$inboundSchema),
      resource_urns: z.array(z.string()),
      resources: z.array(ResourceEntry$inboundSchema),
      security_variables: z.optional(z.array(SecurityVariable$inboundSchema)),
      server_variables: z.optional(z.array(ServerVariable$inboundSchema)),
      slug: z.string(),
      tool_selection_mode: z.string(),
      tool_urns: z.array(z.string()),
      tools: z.array(ToolEntry$inboundSchema),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "custom_domain_id": "customDomainId",
        "default_environment_slug": "defaultEnvironmentSlug",
        "external_mcp_header_definitions": "externalMcpHeaderDefinitions",
        "function_environment_variables": "functionEnvironmentVariables",
        "mcp_enabled": "mcpEnabled",
        "mcp_is_public": "mcpIsPublic",
        "mcp_slug": "mcpSlug",
        "organization_id": "organizationId",
        "project_id": "projectId",
        "prompt_templates": "promptTemplates",
        "resource_urns": "resourceUrns",
        "security_variables": "securityVariables",
        "server_variables": "serverVariables",
        "tool_selection_mode": "toolSelectionMode",
        "tool_urns": "toolUrns",
        "updated_at": "updatedAt",
      });
    }),
  );

export function toolsetEntryFromJSON(
  jsonString: string,
): SafeParseResult<ToolsetEntry, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ToolsetEntry$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ToolsetEntry' from JSON`,
  );
}
